import { type ValidationRuleWithParams } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';

import { isIS24User } from '@/helpers/userUtils';
import { CustomerSegment } from '@/models/customerSegments';
import { Category, Country } from '@smg-real-estate/hg-rets-schema';

/**
 * Validates an email address using a strict ASCII-only pattern.
 * - Allows alphanumeric characters and common email symbols in the local part (no asterisks; masked placeholders are invalid).
 * - Disallows international (non-ASCII) characters like ü, ñ, or 你好.
 * - Requires a domain with only ASCII letters, digits, and hyphens.
 * - Ensures the domain ends with a valid Top-Level Domain of at least two letters.
 */
export const isHgRetsEmailFormat = helpers.regex(
  /^[A-Za-z0-9!#$%&'+/=?^_`{|}~.-]+@([A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/,
);

export function isDateFormat(value: string | undefined): boolean {
  // cover AvailableFrom.BY_AGREEMENT usecase
  if (value === undefined) {
    return true;
  }

  const dateMatch: RegExpMatchArray | null = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  return !!dateMatch;
}

export function isYoutubeUrl(value: string | undefined): boolean {
  if (!value) {
    return true;
  }

  let match: RegExpMatchArray | null = value.match(
    /^http(?:s?):\/\/(?:www\.)?(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/,
  );

  if (isIS24User()) {
    match = value.match(/^http(?:s?):\/\/(?:www\.)?youtu(?:be\.com\/watch\?v=|\.be\/)[\w\-]+$/);
  }

  return !!match;
}

// eslint-disable-next-line complexity
export function isValidDate(value: string | undefined) {
  // cover AvailableFrom.BY_AGREEMENT usecase
  if (value === undefined) {
    return true;
  }

  const dateMatch: RegExpMatchArray | null = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!dateMatch) {
    return false;
  }

  const [, dateDay, dateMonth, dateYear] = dateMatch.map((item) => parseInt(item, 10));

  // The following logic from https://stackoverflow.com/a/6178341

  // Check the ranges of month and year
  if (dateYear < 1000 || dateYear > 3000 || dateMonth == 0 || dateMonth > 12) {
    return false;
  }

  const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Adjust for leap years
  if (dateYear % 400 == 0 || (dateYear % 100 != 0 && dateYear % 4 == 0)) {
    monthLength[1] = 29;
  }

  // Check the range of the day
  return dateDay <= 0 || dateDay > monthLength[dateMonth - 1] ? false : true;
}

export function isFutureDate(
  value: string | undefined,
  { includeToday }: { includeToday: boolean } = { includeToday: true },
) {
  // cover AvailableFrom.BY_AGREEMENT usecase
  if (value === undefined) {
    return true;
  }

  const dateMatch: RegExpMatchArray | null = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!dateMatch) {
    return false;
  }

  const [, dateDay, dateMonth, dateYear] = dateMatch.map((item) => parseInt(item, 10));

  let futureDate;
  try {
    futureDate = new Date(dateYear, dateMonth - 1, dateDay);
  } catch {
    return false;
  }

  // Initialising a new Date with Date.now() allows easy mocks for testing.
  const currentTime = new Date(Date.now());

  const currentDate = new Date(
    currentTime.getFullYear(),
    currentTime.getMonth(),
    currentTime.getDate(),
  );

  if (includeToday) {
    return futureDate >= currentDate;
  }
  return futureDate > currentDate;
}

function stripHtml(html: string) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

export function htmlTextLengthBetween(
  min: number,
  max: number,
  options: Record<string, boolean> = {
    allowEmpty: false,
  },
): ValidationRuleWithParams {
  return helpers.withParams(
    { type: 'htmlTextLengthBetween', min: min, max: max },
    (value: string | undefined) => {
      if (!value && value !== '') {
        return options.allowEmpty;
      }

      const text = stripHtml(value);

      if (!text && options.allowEmpty) {
        return true;
      }
      return text.length >= min && text.length <= max;
    },
  );
}

export const inputStartsWithLetter = helpers.regex(/^((?!_)[A-z])+/);

/**
 * At least two whitespace-separated tokens; each token must contain a Unicode letter.
 * Empty input passes; use `required` separately.
 */
const UNICODE_LETTER = /\p{L}/u;

export function isFullName(value: string | undefined): boolean {
  if (!value?.trim()) {
    return true;
  }

  const tokens = value.trim().split(/\s+/);
  if (tokens.length < 2) {
    return false;
  }

  return tokens.every((token) => UNICODE_LETTER.test(token));
}

export function isEmailWhitelisted(email: string): boolean {
  const emailDomainWhitelist = ['@homegate.ch', '@swissmarketplace.group'];

  return emailDomainWhitelist.some((whitelistedEmail) => email.includes(whitelistedEmail));
}

const VALID_SIMPLIFIED_LANDING_OFFERS = ['SALE', 'RENT'] as const;
const VALID_SIMPLIFIED_CUSTOMER_SEGMENTS = Object.values(CustomerSegment);

export function isSimplifiedLandingPageQueryDataValid(queryData: {
  offer?: string;
  customerSegment?: string;
}): boolean {
  return (
    VALID_SIMPLIFIED_LANDING_OFFERS.includes(
      queryData.offer as (typeof VALID_SIMPLIFIED_LANDING_OFFERS)[number],
    ) && VALID_SIMPLIFIED_CUSTOMER_SEGMENTS.includes(queryData.customerSegment as CustomerSegment)
  );
}

export function isLandingPageQueryDataValid(
  queryData: any,
  options?: { simplified?: boolean },
): boolean {
  if (options?.simplified) {
    return isSimplifiedLandingPageQueryDataValid(queryData);
  }

  if (Country.Ch === queryData.country && !queryData.canton) {
    return false;
  }

  return (
    !!queryData.offer &&
    !!queryData.type &&
    !!queryData.category &&
    !!queryData.country &&
    !!queryData.zip &&
    !!queryData.city
  );
}

export function isRoomsRequired(categories: Category[] = []): boolean {
  const isHouseExceptHouseDwelling =
    categories.includes(Category.House) && !categories.includes(Category.MultipleDwelling);
  const isApartment = categories.includes(Category.Apartment);
  const isSharedApartment = categories.includes(Category.SharedApartment);
  return isHouseExceptHouseDwelling || isApartment || isSharedApartment;
}
